import React, { useEffect, useRef, useState } from 'react';
import { Star, Check } from 'lucide-react';

const Products: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const products = [
    {
      name: "Cobertura Sol Nascente",
      image: "/img/carrosel/imagem1.jpeg",
      description: "Distribuímos hortaliças frescas diretamente de Ibiúna-SP para as principais regiões da Grande São Paulo e Litoral. Confira as cidades e bairros atendidos:",
      features: [
        "Centro de distribuição em Ibiúna-SP",
        "Hortaliças frescas",
        "Atendimento na Grande São Paulo e Litoral",
        "Mais de 70 anos de tradição"
      ],
      category: "Cobertura",
      highlight: true
    },
    {
      name: "Couve Manteiga",
      image: "/img/carrosel/imagem2.jpeg",
      description: "Couve manteiga fresca, folhas largas e macias, ideal para refogados, sucos verdes e receitas tradicionais. Cultivada com cuidado para garantir sabor e textura.",
      features: [
        "Folhas largas e tenras",
        "Sabor marcante",
        "Ideal para receitas tradicionais",
        "Colheita diária"
      ],
      category: "Folhosas"
    },
    {
      name: "Repolho",
      image: "/img/carrosel/imagem3.jpeg",
      description: "Repolho verde fresco, crocante e nutritivo. Perfeito para saladas, refogados e pratos típicos. Sempre colhido no ponto certo.",
      features: [
        "Crocante e fresco",
        "Rico em fibras",
        "Versátil na cozinha",
        "Cultivo sustentável"
      ],
      category: "Crucíferas"
    },
    {
      name: "Vagem",
      image: "/img/carrosel/imagem4.jpeg",
      description: "Vagem tenra, saborosa e colhida diariamente. Excelente para saladas, cozidos e acompanhamentos leves.",
      features: [
        "Textura macia",
        "Sabor suave",
        "Colheita fresca",
        "Ideal para pratos leves"
      ],
      category: "Leguminosas"
    },
    {
      name: "Couve-flor e Brócolis Ninja",
      image: "/img/carrosel/imagem5.jpeg",
      description: "Couve-flor e brócolis ninja frescos, ricos em nutrientes e perfeitos para receitas saudáveis. Cultivo sem agrotóxicos.",
      features: [
        "Ricos em nutrientes",
        "Cultivo sustentável",
        "Frescor garantido",
        "Versáteis na cozinha"
      ],
      category: "Crucíferas"
    }
  ];

  const placeholderImages = [
    "https://images.unsplash.com/photo-1542838132-92c53300491e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1518977676601-b53f82aba655?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1447279506476-3faec8071eee?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1515485244153-4b1e12e9b651?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
  ];

  return (
    <section ref={sectionRef} id="products" className="py-20 bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
            Nossos <span className="text-gradient">Produtos</span>
          </h2>
          <p className="text-xl text-primary-700 max-w-3xl mx-auto">
            Hortaliças frescas, selecionadas e cultivadas com todo o carinho e expertise 
            de mais de 70 anos de tradição familiar
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <div 
              key={index}
              className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group hover:scale-105 ${
                product.highlight ? 'ring-2 ring-primary-500 ring-opacity-50' : ''
              } ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.src = placeholderImages[index];
                  }}
                />
                {product.highlight && (
                  <div className="absolute top-4 right-4 bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <Star size={16} className="fill-current" />
                    Destaque
                  </div>
                )}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-primary-800">
                  {product.category}
                </div>
              </div>

              {/* Product Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary-800 mb-3">{product.name}</h3>
                <p className="text-primary-600 mb-4 leading-relaxed">{product.description}</p>
                
                {/* Features */}
                <div className="space-y-2 mb-6">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Check className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-sm text-primary-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button removido conforme solicitado */}
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-800 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              Produtos Sempre Frescos
            </h3>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              Todos os nossos produtos são colhidos diariamente e chegam até você com o máximo de frescor e qualidade. 
              Experimente a diferença de quem tem 70 anos de tradição!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://wa.me/551532415413?text=Olá! Gostaria de falar com o setor comercial (Gabriel) da Sol Nascente Hortaliças." 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-200 flex items-center justify-center gap-3"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.109"/>
                </svg>
                <span>Fazer Pedido</span>
              </a>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Falar Conosco
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
